import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutDashboard, Mail, Users, Heart, Newspaper, LogOut, ExternalLink, Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";

const items = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "admin-nav-overview" },
    { to: "/admin/donations", label: "Donations", icon: Heart, testid: "admin-nav-donations" },
    { to: "/admin/memberships", label: "Memberships", icon: Users, testid: "admin-nav-memberships" },
    { to: "/admin/contacts", label: "Contacts", icon: Mail, testid: "admin-nav-contacts" },
    { to: "/admin/newsletter", label: "Newsletter", icon: Newspaper, testid: "admin-nav-newsletter" },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const doLogout = () => {
        logout();
        navigate("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-caireen-cream text-caireen-ink">
            {/* Mobile topbar */}
            <header className="lg:hidden sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-caireen-ink/10 px-5 py-3 flex items-center justify-between">
                <Link to="/admin" className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-caireen-orange/30">
                        <img src="/caireen-logo.jpg" alt="" className="w-full h-full object-cover" />
                    </span>
                    <span className="font-serif text-base">Caireen Admin</span>
                </Link>
                <button onClick={() => setOpen((v) => !v)} className="p-2 rounded-full hover:bg-caireen-ink/5" aria-label="Menu">
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </header>

            <div className="flex">
                {/* Sidebar */}
                <aside
                    className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-white border-r border-caireen-ink/10 transition-transform lg:translate-x-0 ${
                        open ? "translate-x-0" : "-translate-x-full"
                    }`}
                    data-testid="admin-sidebar"
                >
                    <div className="h-full flex flex-col">
                        <div className="px-6 pt-7 pb-6 border-b border-caireen-ink/10">
                            <Link to="/admin" className="flex items-center gap-3" onClick={() => setOpen(false)}>
                                <span className="w-11 h-11 rounded-full overflow-hidden ring-2 ring-caireen-orange/30">
                                    <img src="/caireen-logo.jpg" alt="Caireen" className="w-full h-full object-cover" />
                                </span>
                                <div className="leading-tight">
                                    <p className="font-serif text-lg">Caireen Verein</p>
                                    <p className="font-sans text-[10px] tracking-[0.18em] uppercase text-caireen-ink/60">Admin Console</p>
                                </div>
                            </Link>
                        </div>

                        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
                            {items.map((it) => (
                                <NavLink
                                    key={it.to}
                                    to={it.to}
                                    end={it.end}
                                    onClick={() => setOpen(false)}
                                    data-testid={it.testid}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-4 py-3 rounded-xl font-sans text-sm font-medium transition-colors ${
                                            isActive
                                                ? "bg-caireen-orange/10 text-caireen-orange"
                                                : "text-caireen-ink/75 hover:bg-caireen-ink/5"
                                        }`
                                    }
                                >
                                    <it.icon className="w-4 h-4" strokeWidth={1.75} />
                                    {it.label}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="px-3 py-4 border-t border-caireen-ink/10 space-y-1">
                            <Link
                                to="/"
                                target="_blank"
                                rel="noopener"
                                className="flex items-center gap-3 px-4 py-3 rounded-xl font-sans text-sm text-caireen-ink/70 hover:bg-caireen-ink/5"
                            >
                                <ExternalLink className="w-4 h-4" strokeWidth={1.75} />
                                Public site
                            </Link>
                            <div className="px-4 py-2 text-xs font-sans text-caireen-ink/55 truncate">
                                {user?.email}
                            </div>
                            <button
                                onClick={doLogout}
                                data-testid="admin-logout"
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-sans text-sm font-medium text-caireen-red hover:bg-caireen-red/5 transition-colors"
                            >
                                <LogOut className="w-4 h-4" strokeWidth={1.75} />
                                Sign out
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Backdrop */}
                {open && (
                    <div
                        className="lg:hidden fixed inset-0 z-30 bg-caireen-ink/40"
                        onClick={() => setOpen(false)}
                    />
                )}

                {/* Content */}
                <main className="flex-1 min-w-0">
                    <motion.div
                        key={typeof window !== "undefined" ? window.location.pathname : "admin"}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-6 md:px-10 py-8 md:py-10 max-w-[1400px] mx-auto"
                    >
                        <Outlet />
                    </motion.div>
                </main>
            </div>

            <Toaster richColors position="top-center" />
        </div>
    );
}
